<template>
  <div class="breadcrumbs">
    <hr-breadcrumbs :items="breadcrumbItems" />
  </div>
</template>

<script>
import { camelCase, chain, forEach, head, includes, slice, without } from 'lodash';
import { mapState } from 'pinia';
import { HrBreadcrumbs } from 'src/design-system';
import { auth } from 'src/store/auth';

export default {
  name: 'Breadcrumbs',

  components: {
    HrBreadcrumbs,
  },

  props: {
    // In case dictionary path doesn't look like url path:
    // 'foo.baz.breadcrumbElLabel' == /#/foo/baz
    customPageLabel: {
      type: String,
      default: null,
    },

    // Ignores as many recent pages in directories as were passed in argument.
    //
    // In case we don't need to show some last pages in breadcrumbs.
    // Find example at ViewSession.vue page (url path not suitable for this component logic)
    ignoreLastPages: {
      type: Number,
      default: null,
    },

    // In case we need to route by clicking last directory in directories
    routeLastPage: {
      type: Boolean,
      default: false,
    },

    // Hide client breadcrumbs element
    hideClient: {
      type: Boolean,
      default: false,
    },

    // Don't show link on these pages
    noLink: {
      type: Array,
      default: () => ([]),
    },

    // Custom breadcrumb structure overrides default route-based structure.
    // Accepts array of strings or objects { label: string, to?: routeObj | string }
    customBreadcrumbs: {
      type: Array,
      default: null,
      validator(value) {
        if (!Array.isArray(value)) {
          return false;
        }

        return value.every((item) => {
          // String breadcrumbs are valid
          if (typeof item === 'string') {
            return true;
          }

          // Object breadcrumbs must have a string label
          if (typeof item?.label !== 'string') {
            return false;
          }

          // If `to` property exists, it must be a string or an object with `name` (route object)
          if (item?.to && (typeof item.to !== 'string' && typeof item.to?.name !== 'string')) {
            return false;
          }

          return true;
        });
      },
    },

  },

  computed: {
    ...mapState(auth, ['currentClient']),

    mainPageDirectoryName() {
      return head(this.directories);
    }, // mainPageDirectoryName()

    mainPageLabel() {
      return this.customPageLabel || this.$t(`${camelCase(this.mainPageDirectoryName)}.breadcrumbElLabel`);
    }, // mainPageLabel()

    allDirectories() {
      return without(this.$route.path.split('/'), '');
    }, // allDirectories()

    directoriesWithoutLasts() {
      return slice(this.allDirectories, 0, this.allDirectories.length - this.ignoreLastPages);
    }, // directoriesWithoutLasts()

    directories() {
      return this.ignoreLastPages ? this.directoriesWithoutLasts : this.allDirectories;
    }, // directories()

    hasSubdirectories() {
      return this.directories.length > 1;
    }, // hasSubdirectories()

    isSettingsPage() {
      return this.mainPageDirectoryName === 'settings';
    }, // isSettingsPage()

    hasCustomBreadcrumbs() {
      return Array.isArray(this.customBreadcrumbs) && this.customBreadcrumbs.length > 0;
    }, // hasCustomBreadcrumbs()

    breadcrumbItems() {
      const items = [];

      if (!this.isSettingsPage && !this.hideClient) {
        items.push({ label: this.currentClient.clientName, to: { name: 'Home' } });
      }

      if (this.hasCustomBreadcrumbs) {
        this.customBreadcrumbs.forEach((crumb) => {
          items.push({
            label: this.getCustomBreadcrumbLabel(crumb),
            to: this.getCustomBreadcrumbRoute(crumb),
          });
        });
        return items;
      }

      if (this.hasSubdirectories) {
        this.directories.forEach((directory, index) => {
          items.push({
            label: this.getPageLabel(directory, index),
            to: this.getToRouteObj(index),
          });
        });
      } else {
        items.push({ label: this.mainPageLabel });
      }

      return items;
    }, // breadcrumbItems()

  },

  methods: {
    isLastDirectory(index) {
      return (index + 1) === this.directories.length;
    }, // isLastDirectory()

    getPageLabel(directory, index) {
      if (this.customPageLabel && this.isLastDirectory(index)) {
        return this.customPageLabel;
      }

      if (this.hasSubdirectories) {
        let directoryPath = '';

        forEach(this.directories, (directoryName, idx) => {
          if (idx <= index) {
            directoryPath = idx === 0
              ? camelCase(directoryName)
              : `${directoryPath}.${camelCase(directoryName)}`;
          }
        });

        return this.$te(`${directoryPath}.breadcrumbElLabel`)
          ? this.$t(`${directoryPath}.breadcrumbElLabel`)
          : this.mainPageLabel;
      }

      return this.$t(`${directory}.breadcrumbElLabel`);
    }, // getPageLabel()

    getToRouteObj(index) {
      if (this.isLastDirectory(index) && !this.routeLastPage) {
        return null;
      }
      if (this.hasSubdirectories) {
        const path = `/${this.directories.slice(0, (index + 1)).join('/')}`;

        if (includes(this.noLink, chain(this.directories).slice(0, (index + 1)).last().value())) {
          return null;
        }
        return { path };
      }

      return { path: `/${this.mainPageDirectoryName}` };
    }, // getToRouteObj()

    getCustomBreadcrumbLabel(crumb) {
      return typeof crumb === 'string' ? crumb : crumb.label;
    }, // getCustomBreadcrumbLabel()

    getCustomBreadcrumbRoute(crumb) {
      return crumb?.to || null;
    }, // getCustomBreadcrumbRoute()
  },

};
</script>

<style lang="scss" scoped>
.breadcrumbs {
  padding-bottom: var(--app-comp-page-padding-md);
}
</style>
